/**
 * @file useDropdownPositioning — trigger measurement + panel geometry for the
 * FixedPositionDropdownShell atom.
 * @description The single owner of the measure/clamp/remeasure logic that was
 * previously copy-pasted across the update-feed CategoryFilterDropdown, the
 * scouting ListSelector, and the player-kanban board toolbar's dropdowns
 * (promoted + generalized from `features/player-kanban/hooks/
 * useDropdownPositioning`, SE-5528):
 *
 * - **Web** — measures the trigger in viewport coordinates (RN-web
 *   `measureInWindow`, falling back to DOM `getBoundingClientRect` for raw
 *   element refs), computes a `position: fixed` panel geometry anchored to
 *   the trigger's left or right edge, clamps it to an 8px viewport margin,
 *   and re-measures on scroll/resize while open so the portalled panel never
 *   strands at stale coordinates.
 * - **Native (SE-5568)** — measures the trigger in WINDOW coordinates via
 *   `measureInWindow` and computes the same clamped geometry; the shell Panel
 *   renders it inside a transparent RN `Modal`, whose window shares that
 *   coordinate space. (The previous static `top: nativeTopOffset` absolute
 *   strategy was device-disproven: on the RN new architecture the panel
 *   anchored to whichever ancestor carried an explicit position style, and an
 *   in-tree absolute panel can never out-stack later elevated subtrees such
 *   as the global FAB — the Modal window can.)
 *
 * `shouldShow` encodes the SE-4953 flicker guard: on both platforms the
 * panel must not render until the async measurement lands (otherwise it
 * flashes at fallback coordinates and jumps). The measured rect is reset in
 * the open-effect's cleanup, so every re-open re-runs the guard instead of
 * reusing stale coordinates.
 * @module components/atoms/FixedPositionDropdownShell/useDropdownPositioning
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Platform, StatusBar, useWindowDimensions } from "react-native";

import { useKeyboardHeight } from "@/hooks/useKeyboardHeight";

/** Cached platform flag — geometry strategy (fixed vs absolute) hangs off it. */
const isWeb = Platform.OS === "web";

/**
 * Android-only Y offset between the app window (where `measureInWindow`
 * coordinates originate — the window starts BELOW the status bar) and the
 * Panel's RN Modal window (forced edge-to-edge by react-native-edge-to-edge,
 * so it starts at the physical screen top). Device-measured on SE-5568: a
 * trigger at screen y=559.7dp reported y=528.7dp — exactly the 31dp status
 * bar. iOS app windows are full-screen, so the spaces already align.
 * Read on every measurement (not cached at module load) — `StatusBar.
 * currentHeight` can change after an orientation change or a display-cutout
 * transition, and a module-load-time snapshot would bake in a stale value
 * for the lifetime of the app.
 * @returns The current Android status-bar inset in px, or 0 on iOS/web.
 */
function getNativeWindowTopInset(): number {
  return Platform.OS === "android" ? (StatusBar.currentHeight ?? 0) : 0;
}

/** Minimum gap kept between the panel and every viewport edge (px). */
const VIEWPORT_MARGIN = 8;

/** Default pixel gap between the trigger's bottom edge and the panel's top edge. */
const DEFAULT_PANEL_OFFSET = 4;

/** Viewport-coordinate rectangle for the measured trigger element. */
export interface DropdownTriggerRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Style-ready panel geometry. On web: viewport coordinates for a
 * `position: fixed` panel. On native (SE-5568): window coordinates for the
 * absolute panel inside the shell Panel's transparent RN Modal.
 * Exactly one of `top` (panel below the trigger) or `bottom` (panel flipped
 * above the trigger, growing upward) is set.
 */
export interface DropdownPanelGeometry {
  readonly top?: number;
  readonly bottom?: number;
  readonly left?: number;
  readonly right?: number;
  readonly width?: number;
  readonly minWidth?: number;
  readonly maxHeight?: number;
}

/**
 * Minimal measurable surface of a trigger node — RN(-web) views expose
 * `measureInWindow`, raw DOM elements expose `getBoundingClientRect`.
 */
export interface DropdownMeasurableNode {
  measureInWindow?: (
    callback: (x: number, y: number, width: number, height: number) => void
  ) => void;
  getBoundingClientRect?: () => {
    left: number;
    top: number;
    width: number;
    height: number;
  };
}

/** Parameters for {@link useDropdownPositioning}. */
export interface UseDropdownPositioningParams {
  /** Whether the dropdown is open — measurement only runs while open. */
  readonly isOpen: boolean;
  /**
   * Externally-owned trigger ref (headless mode, e.g. the kanban top-bar
   * filter button that lives outside the dropdown component). Must be an
   * object ref — the hook reads `.current` to measure, so callback refs
   * could never be measured. Omit to use the returned `triggerRef` on a
   * shell Trigger.
   */
  readonly externalTriggerRef?: React.RefObject<DropdownMeasurableNode | null> | null;
  /** Which trigger edge the panel hugs. Defaults to `"left"`. */
  readonly anchor?: "left" | "right";
  /**
   * Fixed panel width in px (also applied to the panel style). On web the
   * rendered width is clamped to the viewport minus both 8px margins.
   */
  readonly panelWidth?: number;
  /**
   * Minimum panel width in px — the effective anchoring width becomes
   * `max(panelMinWidth, trigger width)` (also applied to the panel style).
   * On web it is clamped to the viewport minus both 8px margins.
   */
  readonly panelMinWidth?: number;
  /**
   * Estimated panel height in px — enables bottom-edge clamping and is
   * applied to the panel style as `maxHeight`.
   */
  readonly panelHeight?: number;
  /** Gap between trigger bottom and panel top (px). Defaults to 4. */
  readonly panelOffset?: number;
  /**
   * Deprecated (SE-5568) — ignored. Native now measures the trigger via
   * `measureInWindow` like web; accepted for signature compatibility.
   */
  readonly nativeTopOffset?: number;
  /**
   * Re-measures the trigger whenever this value changes while open. Scroll
   * and resize already re-measure; this covers the trigger MOVING because the
   * layout around it changed — e.g. a centred bar whose sentence grows while
   * its menu is open (SE-8526), which would strand the panel otherwise.
   */
  readonly remeasureKey?: unknown;
}

/** Return shape of {@link useDropdownPositioning}. */
export interface UseDropdownPositioningReturn {
  /** Attach to the shell Trigger (unless an external ref was supplied). */
  readonly triggerRef: React.RefObject<DropdownMeasurableNode | null>;
  /** Style-ready panel geometry, or null before web measurement lands. */
  readonly panelGeometry: DropdownPanelGeometry | null;
  /** SE-4953 flicker guard — render Backdrop/Panel only when true. */
  readonly shouldShow: boolean;
  /** Raw measured trigger rect (web), for callers needing the trigger box. */
  readonly triggerRect: DropdownTriggerRect | null;
}

/**
 * Predicate behind the shell's render guard, kept exported for unit tests and
 * for consumers that need the raw form (SE-4953 flicker fix): on both
 * platforms the panel renders only after the async trigger measurement
 * resolves (SE-5568 — native now measures via `measureInWindow` for the
 * Modal-hosted panel; the `isWebPlatform` parameter is kept for signature
 * compatibility).
 * @param isOpen - Whether the dropdown is in the open state.
 * @param _isWebPlatform - Unused (kept for signature compatibility).
 * @param triggerRect - Measured viewport rect, or `null` if not yet measured.
 * @returns `true` when the dropdown overlay should render.
 */
export function shouldShowDropdownTree(
  isOpen: boolean,
  _isWebPlatform: boolean,
  triggerRect: DropdownTriggerRect | null
): boolean {
  return isOpen && triggerRect !== null;
}

/**
 * Measures a trigger node into viewport coordinates, preferring the RN(-web)
 * `measureInWindow` API and falling back to DOM `getBoundingClientRect`.
 * Kept module-level so the state write stays a measurement callback (not a
 * synchronous set inside the effect body).
 * @param node - The trigger node to measure.
 * @param onRect - Receives the measured rect (async for measureInWindow).
 */
function measureNode(
  node: DropdownMeasurableNode,
  onRect: (rect: DropdownTriggerRect) => void
): void {
  if (typeof node.measureInWindow === "function") {
    node.measureInWindow((x, y, width, height) => {
      onRect({ x, y, width, height });
    });
    return;
  }
  if (typeof node.getBoundingClientRect === "function") {
    const rect = node.getBoundingClientRect();
    onRect({
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height,
    });
  }
}

/** Inputs for {@link computeWebPanelGeometry}. */
interface WebPanelGeometryInput {
  readonly triggerRect: DropdownTriggerRect;
  readonly anchor: "left" | "right";
  readonly panelWidth?: number;
  readonly panelMinWidth?: number;
  readonly panelHeight?: number;
  readonly panelOffset: number;
  readonly viewportWidth: number;
  /**
   * Height the panel must FIT IN — the window minus anything covering it,
   * which today means the keyboard (SE-7388).
   */
  readonly viewportHeight: number;
  /**
   * Height the panel is POSITIONED AGAINST — the window's own, which does not
   * shrink when the keyboard opens.
   *
   * Separate from `viewportHeight` on purpose. `bottom` is measured from the
   * window's bottom edge, so computing it from the covered height would push
   * the flipped panel DOWN by exactly the keyboard's height — trading one
   * defect for another. Fit is one question; coordinates are another.
   * Defaults to `viewportHeight`, which is what every caller with no keyboard
   * up already means.
   */
  readonly windowHeight?: number;
}

/**
 * Computes the panel geometry from a measured trigger rect, clamping BOTH
 * the horizontal position and the rendered width to the 8px viewport
 * margins: on small viewports (e.g. a 500px panel at 375px wide) clamping
 * `left` alone would still paint the panel through the right margin, so the
 * effective width is capped to `viewport - 2×margin` and the same clamped
 * value feeds the left clamp AND the returned width so they stay consistent.
 * The width is always emitted explicitly (`panelWidth`, else
 * `max(panelMinWidth, trigger width)`) so the panel renders at a predictable
 * trigger-derived width instead of stretching to its `max-w-*` cap inside
 * the unbounded native Modal window (SE-5568 device finding).
 *
 * Vertical placement (when `panelHeight` is known): prefer opening BELOW the
 * trigger; when the panel doesn't fit below but has more room above, it
 * FLIPS above the trigger — anchored to the trigger's top edge via `bottom`
 * so a shorter-than-max panel stays attached — mirroring the old gluestack
 * Menu `shouldFlip`. Whichever side is used, `maxHeight` is reduced to the
 * available space so the panel never covers its own trigger or runs past
 * the viewport edge (SE-5568 device finding: the previous hard clamp slid
 * the panel over the trigger).
 * Module-level pure function (keeps the hook's memo trivially simple).
 * @param input - Measured rect, sizing config, and viewport dimensions.
 * @returns Style-ready fixed-position geometry.
 */
function computeWebPanelGeometry(
  input: WebPanelGeometryInput
): DropdownPanelGeometry {
  const {
    triggerRect,
    anchor,
    panelWidth,
    panelMinWidth,
    panelHeight,
    panelOffset,
    viewportWidth,
    viewportHeight,
  } = input;
  const windowHeight = input.windowHeight ?? viewportHeight;

  const maxPanelWidth = viewportWidth - 2 * VIEWPORT_MARGIN;
  const effectiveWidth = Math.min(
    panelWidth ?? Math.max(panelMinWidth ?? 0, triggerRect.width),
    maxPanelWidth
  );
  const baseLeft =
    anchor === "right"
      ? triggerRect.x + triggerRect.width - effectiveWidth
      : triggerRect.x;
  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(baseLeft, viewportWidth - effectiveWidth - VIEWPORT_MARGIN)
  );

  const horizontal = {
    left,
    width: effectiveWidth,
    ...(panelHeight === undefined ? {} : { maxHeight: panelHeight }),
  };

  const baseTop = triggerRect.y + triggerRect.height + panelOffset;
  if (panelHeight !== undefined) {
    const spaceBelow = viewportHeight - VIEWPORT_MARGIN - baseTop;
    if (panelHeight > spaceBelow) {
      const spaceAbove = triggerRect.y - panelOffset - VIEWPORT_MARGIN;
      if (spaceAbove > spaceBelow) {
        // Flip above: anchor the panel's bottom edge to the trigger's top.
        return {
          ...horizontal,
          bottom: windowHeight - triggerRect.y + panelOffset,
          maxHeight: Math.min(panelHeight, spaceAbove),
        };
      }
      // Stay below with whatever room is left; content scrolls inside.
      return {
        ...horizontal,
        top: baseTop,
        maxHeight: Math.max(0, spaceBelow),
      };
    }
  }

  return { ...horizontal, top: baseTop };
}

/**
 * Owns trigger measurement, viewport clamping, and remeasure-on-scroll/resize
 * for the FixedPositionDropdownShell. See the file doc comment for the full
 * web/native strategy.
 * @param params - Hook configuration.
 * @param params.isOpen - Whether the dropdown is open.
 * @param params.externalTriggerRef - Externally-owned trigger ref (headless mode).
 * @param params.anchor - Which trigger edge the panel hugs (default "left").
 * @param params.panelWidth - Fixed panel width in px.
 * @param params.panelMinWidth - Minimum panel width in px.
 * @param params.panelHeight - Estimated panel height in px (bottom clamping).
 * @param params.panelOffset - Gap between trigger bottom and panel top (px).
 * @param params.nativeTopOffset - Deprecated (SE-5568) — ignored; native now
 * measures the trigger like web. Accepted for signature compatibility.
 * @param params.remeasureKey - Re-measures the trigger when it changes while open.
 * @returns Trigger ref, style-ready panel geometry, and the render guard.
 */
export default function useDropdownPositioning({
  isOpen,
  externalTriggerRef,
  anchor = "left",
  panelWidth,
  panelMinWidth,
  panelHeight,
  panelOffset = DEFAULT_PANEL_OFFSET,
  remeasureKey,
}: UseDropdownPositioningParams): UseDropdownPositioningReturn {
  const { width: viewportWidth, height: windowHeight } = useWindowDimensions();
  const keyboardHeight = useKeyboardHeight();
  // The panel has to fit in what the user can SEE, not in what the window
  // claims to be (SE-7388). With the keyboard down this is the window height,
  // so the desktop and closed-keyboard paths are unchanged.
  const viewportHeight = windowHeight - keyboardHeight;
  const internalTriggerRef = useRef<DropdownMeasurableNode | null>(null);
  const triggerRef = externalTriggerRef ?? internalTriggerRef;
  const [triggerRect, setTriggerRect] = useState<DropdownTriggerRect | null>(
    null
  );
  /**
   * Measurement generation token — bumped on every (re)measure and on the
   * open-effect's cleanup. `measureInWindow` resolves asynchronously, so a
   * callback from an older remeasure (or from a closed/unmounted cycle) can
   * land late and repopulate stale coordinates; callbacks whose generation is
   * no longer current are dropped instead.
   */
  const measureGenerationRef = useRef(0);
  /** The open cycle's `measure`, so a `remeasureKey` change can re-run it. */
  const remeasureRef = useRef<(() => void) | null>(null);
  /** The key the trigger was last measured for. */
  const lastRemeasureKeyRef = useRef(remeasureKey);

  const panelGeometry = useMemo<DropdownPanelGeometry | null>(() => {
    // SE-5568: both platforms use the measured, viewport-clamped geometry.
    // On native the coordinates are window coordinates (`measureInWindow`)
    // consumed by the Panel's transparent RN Modal window.
    if (!triggerRect) return null;

    return computeWebPanelGeometry({
      triggerRect,
      anchor,
      panelWidth,
      panelMinWidth,
      panelHeight,
      panelOffset,
      viewportWidth,
      viewportHeight,
      windowHeight,
    });
  }, [
    anchor,
    panelHeight,
    panelMinWidth,
    panelOffset,
    panelWidth,
    triggerRect,
    viewportHeight,
    viewportWidth,
    windowHeight,
  ]);

  /**
   * Measure at open time and keep the panel pinned to the trigger while the
   * page scrolls or resizes (web) — otherwise the portalled fixed panel
   * strands at the coordinates captured when it opened. The cleanup resets
   * the rect when the dropdown closes, so the next open re-runs the SE-4953
   * pre-measurement guard instead of flashing at stale coordinates.
   */
  useEffect(() => {
    if (!isOpen) return;
    const measure = (): void => {
      const node = triggerRef?.current;
      if (!node) return;
      measureGenerationRef.current += 1;
      const generation = measureGenerationRef.current;
      measureNode(node, rect => {
        /*
         * Drop stale async callbacks: a `measureInWindow` resolution from an
         * older remeasure — or one landing after close/unmount — must not
         * repopulate outdated coordinates.
         */
        if (measureGenerationRef.current === generation) {
          const nativeWindowTopInset = getNativeWindowTopInset();
          setTriggerRect(
            nativeWindowTopInset === 0
              ? rect
              : { ...rect, y: rect.y + nativeWindowTopInset }
          );
        }
      });
    };
    measure();
    remeasureRef.current = measure;
    const detachListeners =
      isWeb && typeof window !== "undefined"
        ? (() => {
            const remeasure = (): void => measure();
            window.addEventListener("scroll", remeasure, true);
            window.addEventListener("resize", remeasure);
            return () => {
              window.removeEventListener("scroll", remeasure, true);
              window.removeEventListener("resize", remeasure);
            };
          })()
        : undefined;
    return () => {
      /* Invalidate any in-flight async measurements from this open cycle. */
      measureGenerationRef.current += 1;
      remeasureRef.current = null;
      detachListeners?.();
      setTriggerRect(null);
    };
  }, [isOpen, triggerRef]);

  // Layout around the trigger changed while open: measure again in place (no
  // reset to null, so the panel moves instead of flashing). Only on a CHANGE —
  // the open effect above already measured for the current key.
  useEffect(() => {
    if (Object.is(lastRemeasureKeyRef.current, remeasureKey)) return;
    lastRemeasureKeyRef.current = remeasureKey;
    remeasureRef.current?.();
  }, [remeasureKey]);

  return {
    triggerRef,
    panelGeometry,
    shouldShow: isOpen && panelGeometry !== null,
    triggerRect,
  };
}
